/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import * as appStrings from '@touchplan/strings';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { OrganizationProject } from 'src/app/app-data/models/OrganizationProject';
import { OrganizationService } from '../../services/organization.service';

@Component({
	selector: 'organization-app-projects',
	templateUrl: './organization-app-projects.component.html',
	styleUrls: ['./organization-app-projects.component.scss'],
})
export class OrganizationAppProjectsComponent implements OnInit, OnChanges, OnDestroy {
	public appStrings = appStrings;
	@Input() public parentFormGroup: UntypedFormGroup;
	public inheritProjects = new UntypedFormControl(false);
	public readonly formGroupName = 'appOrgProjectsFormGroup';
	public projectsFormGroup = new UntypedFormGroup({});
	public appOrgProjectsForm = new UntypedFormGroup({
		appOrgProjectsForm: this.projectsFormGroup,
		inheritProjects: this.inheritProjects,
	});
	public projects$: Observable<OrganizationProject[]>;
	private shutdownSubject$: Subject<boolean> = new Subject();

	constructor(public organizationService: OrganizationService, private formBuilder: UntypedFormBuilder) {}

	public ngOnInit(): void {
		this.projects$ = this.organizationService.application$.pipe(
			map((application) => {
				if (!application || !application.view.selectedProjects) {
					return null;
				}
				const arr = application.view.selectedProjects;
				return arr ? arr.map((s) => s.orgProject) : null;
			})
		);

		//TODO - shift this to two observable chains, one from each form thing, with a "withLatest from from each"
		// don't really want to know when both have emitted, want to know when one emits + the last value from the other
		combineLatest([
			this.inheritProjects.valueChanges.pipe(startWith(this.inheritProjects.value)),
			this.projectsFormGroup.valueChanges,
		])
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe(([allSelected, selectedProjects]) => {
				if (allSelected) {
					this.organizationService.changeOrganizationProjects(null);
				} else {
					//angular 5-11 borked https://github.com/angular/angular/issues/20439#issuecomment-779988084
					if (this.projectsFormGroup.dirty) {
						this.organizationService.changeOrganizationProjects(selectedProjects);
					}
				}
			});

		this.organizationService.application$.pipe(takeUntil(this.shutdownSubject$)).subscribe((application) => {
			if (application && application.view.selectedProjects) {
				this.inheritProjects.setValue(application.view.inheritProjects, { emitEvent: false });
				application.view.selectedProjects.forEach((project) => {
					this.createProjectFormControl(project.orgProject, project.selected);
				});
			}
		});
	}

	// Takes care of connecting this component's FormGroup to the @Input parentFormGroup,
	// AND changing that connection if necessary (if the @Input value changes):
	public ngOnChanges(changes: SimpleChanges): void {
		const parentFormGroupChange = changes['parentFormGroup'];
		if (parentFormGroupChange) {
			const newParentFormGroup = parentFormGroupChange.currentValue as UntypedFormGroup;
			if (!newParentFormGroup) {
				// Parent is gone !  Remove from the old one:
				(this.appOrgProjectsForm.parent as UntypedFormGroup).removeControl(this.formGroupName);
			} else if (
				parentFormGroupChange.isFirstChange ||
				this.appOrgProjectsForm.parent !== newParentFormGroup
			) {
				// Adding to parent for the first time, or the parent has changed:
				this.parentFormGroup.addControl(this.formGroupName, this.appOrgProjectsForm);
			}
		}
	}

	public ngOnDestroy(): void {
		this.parentFormGroup.removeControl(this.formGroupName);
		this.shutdownSubject$.next(true);
		this.shutdownSubject$.complete();
	}

	public createProjectFormControl(project: OrganizationProject, value: boolean) {
		const existingControl = this.projectsFormGroup.get(project.$id);
		// If there is no existing control create a new and assign true value
		if (!existingControl) {
			const newControl = this.formBuilder.control(value);
			this.projectsFormGroup.addControl(project.$id, newControl);
		} else {
			existingControl.setValue(value, { emitEvent: false });
		}
	}
}
